import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import { de, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import type { Chat } from "../chat.service";
import { useChatHistoryGestures } from "./chatHistoryGestures.hook";

interface ChatHistoryProps {
	chatHistory: Chat[];
	currentChatId: string | null;
	currentPodWorkspaceId: string | null;
	isLoadingHistory: boolean;
	onSelectChat: (chatId: string) => void;
	onClose: () => void;
}

/**
 * ChatHistory component
 * Displays a sidebar with all chat history for the current workspace
 * Supports swipe down to close on mobile
 */
export function ChatHistory({
	chatHistory,
	currentChatId,
	currentPodWorkspaceId,
	isLoadingHistory,
	onSelectChat,
	onClose,
}: ChatHistoryProps) {
	const { t, i18n } = useTranslation();

	// Get the appropriate date-fns locale based on current language
	const dateLocale = i18n.language === "de" ? de : enUS;

	// Mobile gesture support for swipe-down to close
	const { dragState, bind } = useChatHistoryGestures({
		onClose,
	});

	return (
		<section
			aria-label="Chat History"
			className="absolute left-0 top-[73px] bottom-0 w-full md:w-80 bg-background border-r border-border z-10 flex flex-col overflow-hidden"
			onMouseDown={(e) => e.stopPropagation()}
			onTouchStart={(e) => e.stopPropagation()}
			style={{
				transform: dragState.isDragging
					? `translateY(${dragState.y}px)`
					: undefined,
				opacity: dragState.opacity,
				transition: dragState.isDragging
					? "none"
					: "transform 0.3s ease-out, opacity 0.3s ease-out",
			}}
		>
			{/* Mobile drag handle - visible on mobile for swipe hint */}
			<div
				className="flex md:hidden items-center justify-center py-2 touch-none cursor-grab active:cursor-grabbing border-b border-border/50"
				{...bind()}
			>
				<div className="w-12 h-1.5 bg-muted-foreground/30 rounded-full" />
			</div>

			<div className="flex items-center justify-between p-4 border-b border-border shrink-0">
				<h4 className="font-semibold text-foreground">
					{t("chat.history.title")}
				</h4>
			</div>
			<ScrollArea className="flex-1 min-h-0" data-history-scroll>
				<div className="h-full">
					{isLoadingHistory ? (
						<div className="flex items-center justify-center p-8">
							<div className="flex items-center gap-2 text-muted-foreground">
								<div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
								<span className="text-sm">{t("chat.history.loading")}</span>
							</div>
						</div>
					) : chatHistory.length === 0 ? (
						<div className="p-8 text-center text-muted-foreground text-sm">
							{t("chat.history.noChats")}
						</div>
					) : (
						<div className="p-2 space-y-1">
							{chatHistory.map((chat) => {
								const isCurrentWorkspace =
									chat.podWorkspaceId === currentPodWorkspaceId;
								const isActiveChat = chat.id === currentChatId;

								return (
									<button
										key={chat.id}
										type="button"
										onClick={() => onSelectChat(chat.id)}
										className={`w-full text-left p-3 rounded-lg transition-colors ${
											isActiveChat
												? "bg-primary/10 border border-primary/20"
												: "hover:bg-muted"
										}`}
									>
										<div className="flex items-start gap-2 mb-1">
											<div className="font-medium text-sm text-foreground line-clamp-1 flex-1">
												{chat.title}
											</div>
											{isCurrentWorkspace && (
												<Badge
													variant="secondary"
													className="text-[10px] px-1.5 py-0 shrink-0"
												>
													{t("chat.history.current")}
												</Badge>
											)}
										</div>
										<div className="text-xs text-muted-foreground">
											{chat.messageCount ?? chat.messages.length}{" "}
											{t("chat.history.message", {
												count: chat.messageCount ?? chat.messages.length,
											})}{" "}
											•{" "}
											{formatDistanceToNow(new Date(chat.createdAt), {
												addSuffix: true,
												locale: dateLocale,
											})}
										</div>
									</button>
								);
							})}
						</div>
					)}
				</div>
			</ScrollArea>
		</section>
	);
}
